import './swap-unit.scss';
import '@fortawesome/fontawesome-free/css/all.css';
import { OrderProduct } from '../../../clients/quipli-service/order/order';
import { Modal } from '../../../components/modal/modal';
import { ProductInventoryUnit } from '../../../clients/quipli-service/product/product';
import { useEffect, useState } from 'react';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { AssignableUnit } from '../../../clients/quipli-service/availability/availability';
import { AssignUnitSelect } from '../assign-unit-select/assign-unit-select';
import { Checkbox } from '../../../components/checkbox/checkbox';
import { Button } from '../../../components/form/button';
import { Field } from '../../../components/field/field';
import Icon from '../../../components/icons/icon';
import { Card } from '../../../components/card/card';
import { DateTime } from 'luxon';

export const SwapUnit = ({
  orderProduct,
  locationId,
  inventoryUnit,
  isOpen,
  onClose,
}: {
  orderProduct: OrderProduct;
  locationId: number;
  inventoryUnit: ProductInventoryUnit;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { swapUnits, getConfiguration } = useAPI();
  const [createOutage, setCreateOutage] = useState(false);
  const [replacementUnitId, setReplacementUnitId] = useState<number>();
  const [hourReading, setHourReading] = useState<number>();
  const [lastEntryOfHourReading, setLastEntryOfHourReading] = useState<DateTime>();
  const [unitTrackingField, setUnitTrackingField] = useState<'unitNumber' | 'serialNumber'>('unitNumber');

  async function submit() {
    if (replacementUnitId) {
      await swapUnits(
        orderProduct.orderId,
        orderProduct.id,
        inventoryUnit.id,
        replacementUnitId,
        createOutage,
        orderProduct.product.allowHourTracking && hourReading ? hourReading : undefined,
      );
      onClose();
    }
  }

  useEffect(() => {
    if (!replacementUnitId) {
      setHourReading(undefined);
      setLastEntryOfHourReading(undefined);
      return;
    }

    const ui = orderProduct.product.inventoryUnits.find((ui) => ui.id === replacementUnitId);
    setHourReading(ui?.lastHourReading?.hour);
    setLastEntryOfHourReading(ui?.lastHourReading?.trackedAt);
  }, [replacementUnitId]);

  useEffect(() => {
    const fetchUnitTrackingConfig = async () => {
      const config = await getConfiguration('CONF_UNIT_TRACKING_VIA');
      setUnitTrackingField(config?.value === '1' ? 'serialNumber' : 'unitNumber');
    };
    fetchUnitTrackingConfig();
  }, []);
  return (
    <Modal
      isOpen={isOpen}
      title="Swap Units"
      actions={
        <>
          <Button className="button button-secondary" onClick={onClose}>
            Cancel
          </Button>

          <Button className="button button-yellow" disabled={!replacementUnitId} onClick={() => submit()}>
            Swap Unit
          </Button>
        </>
      }
      className="!bg-grey-25"
      close={onClose}
    >
      <Card className="py-[14px] px-[12px] flex flex-col gap-[24px] border border-solid border-grey-200 rounded-[8px]">
        <Field label="Current Unit" type="text" onChange={() => {}} value={inventoryUnit[unitTrackingField]} disabled />
      </Card>

      <div className="flex justify-center items-center">
        <Icon icon="SwitchHorizontal" size="xlarge" />
      </div>

      <Card className="py-[14px] px-[12px] flex flex-col gap-[24px] border border-solid border-grey-200 rounded-[8px]">
        <AssignUnitSelect
          isRequired
          label="New Unit"
          locationId={locationId}
          uniqueId={`${orderProduct.id}`}
          productId={orderProduct.product.id}
          quantity={1}
          startDate={DateTime.min(
            DateTime.local({ zone: orderProduct.rentalStartDate.zone }),
            orderProduct.rentalEndDate || DateTime.local({ zone: orderProduct.rentalStartDate.zone }),
          )}
          endDate={orderProduct.rentalEndDate}
          selectedUnitIds={replacementUnitId ? [replacementUnitId] : []}
          onChange={async (unitIds) => {
            setReplacementUnitId(unitIds[0]);
          }}
          isOptionDisabled={(au: AssignableUnit) => ['Available', 'Scheduled'].indexOf(au.status) < 0}
          orderProductType={orderProduct.type}
        />

        {orderProduct.product.allowHourTracking && (
          <Field
            label="Machine Hours"
            type="number"
            onChange={(value) => setHourReading(value)}
            value={hourReading}
            description={lastEntryOfHourReading ? `Last entry/ ${lastEntryOfHourReading?.toFormat('DD')}` : undefined}
          />
        )}
      </Card>

      <Checkbox
        type="yellow"
        label={<span className="!text-grey-700">Create Outage</span>}
        onChange={(isChecked) => setCreateOutage(isChecked)}
        isChecked={createOutage}
      />
    </Modal>
  );
};
